<script setup>
import { onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
    animation: { type: String, default: null },
    intensity: { type: String, default: 'low' },
});

const container = ref(null);
const particles = ref([]);
let animFrame = null;

const intensityMap = { low: 8, medium: 18, high: 30 };

function getCount() {
    return intensityMap[props.intensity] || 8;
}

function createParticles() {
    if (!props.animation || typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const count = getCount();
    const items = [];

    for (let i = 0; i < count; i++) {
        items.push({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 5,
            duration: 3 + Math.random() * 4,
            size: 4 + Math.random() * 8,
            opacity: 0.15 + Math.random() * 0.35,
        });
    }
    particles.value = items;
}

onMounted(() => {
    createParticles();
});

onUnmounted(() => {
    if (animFrame) cancelAnimationFrame(animFrame);
});
</script>

<template>
    <div
        v-if="animation"
        ref="container"
        class="header-animation"
        aria-hidden="true"
    >
        <!-- Snow -->
        <template v-if="animation === 'snow'">
            <div
                v-for="p in particles"
                :key="p.id"
                class="particle snow-particle"
                :style="{
                    left: p.left + '%',
                    animationDelay: p.delay + 's',
                    animationDuration: p.duration + 's',
                    width: p.size + 'px',
                    height: p.size + 'px',
                    '--particle-opacity': p.opacity,
                }"
            />
        </template>

        <!-- Hearts -->
        <template v-if="animation === 'hearts'">
            <div
                v-for="p in particles"
                :key="p.id"
                class="particle heart-particle"
                :style="{
                    left: p.left + '%',
                    animationDelay: p.delay + 's',
                    animationDuration: p.duration + 's',
                    fontSize: p.size + 'px',
                    '--particle-opacity': p.opacity,
                }"
            >&#9829;</div>
        </template>

        <!-- Eggs -->
        <template v-if="animation === 'eggs'">
            <div
                v-for="p in particles"
                :key="p.id"
                class="particle egg-particle"
                :style="{
                    left: p.left + '%',
                    animationDelay: p.delay + 's',
                    animationDuration: (p.duration + 2) + 's',
                    width: (p.size * 1.2) + 'px',
                    height: (p.size * 1.5) + 'px',
                    '--particle-opacity': p.opacity + 0.2,
                    background: ['#FF8A65','#FFD54F','#8BC34A','#BA68C8','#64B5F6'][p.id % 5],
                }"
            />
        </template>

        <!-- Bats -->
        <template v-if="animation === 'bats'">
            <div
                v-for="p in particles"
                :key="p.id"
                class="particle bat-particle"
                :style="{
                    top: (10 + Math.random() * 60) + '%',
                    animationDelay: p.delay + 's',
                    animationDuration: (p.duration + 3) + 's',
                    fontSize: (p.size + 4) + 'px',
                    '--particle-opacity': p.opacity,
                }"
            >&#129415;</div>
        </template>

        <!-- Sparkle -->
        <template v-if="animation === 'sparkle'">
            <div
                v-for="p in particles"
                :key="p.id"
                class="particle sparkle-particle"
                :style="{
                    left: p.left + '%',
                    top: (10 + Math.random() * 70) + '%',
                    animationDelay: p.delay + 's',
                    animationDuration: (1 + Math.random() * 2) + 's',
                    width: (2 + p.size * 0.5) + 'px',
                    height: (2 + p.size * 0.5) + 'px',
                }"
            />
        </template>

        <!-- Fireworks -->
        <template v-if="animation === 'fireworks'">
            <div
                v-for="p in particles"
                :key="p.id"
                class="particle firework-particle"
                :style="{
                    left: p.left + '%',
                    top: (10 + Math.random() * 50) + '%',
                    animationDelay: p.delay + 's',
                    animationDuration: (1.5 + Math.random() * 2) + 's',
                    '--fw-color': ['#FFD700','#FF6B6B','#00B4D8','#06D6A0','#FF6B9D'][p.id % 5],
                }"
            />
        </template>
    </div>
</template>

<style scoped>
.header-animation {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    z-index: 1;
}

.particle {
    position: absolute;
    will-change: transform, opacity;
}

/* ── Snow ── */
.snow-particle {
    background: white;
    border-radius: 50%;
    top: -10px;
    animation: snowfall linear infinite;
}
@keyframes snowfall {
    0% { transform: translateY(-10px) translateX(0); opacity: 0; }
    10% { opacity: var(--particle-opacity, 0.3); }
    90% { opacity: var(--particle-opacity, 0.3); }
    100% { transform: translateY(120px) translateX(20px); opacity: 0; }
}

/* ── Hearts ── */
.heart-particle {
    color: #E91E63;
    bottom: -10px;
    animation: float-up linear infinite;
}
@keyframes float-up {
    0% { transform: translateY(0) scale(0.5); opacity: 0; }
    20% { opacity: var(--particle-opacity, 0.3); }
    100% { transform: translateY(-120px) scale(1); opacity: 0; }
}

/* ── Eggs ── */
.egg-particle {
    border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
    bottom: 0;
    animation: egg-roll linear infinite;
}
@keyframes egg-roll {
    0% { transform: translateX(-30px) rotate(0deg); opacity: 0; }
    10% { opacity: var(--particle-opacity, 0.4); }
    85% { opacity: var(--particle-opacity, 0.4); }
    100% { transform: translateX(60vw) rotate(720deg); opacity: 0; }
}

/* ── Bats ── */
.bat-particle {
    left: -20px;
    animation: bat-fly linear infinite;
}
@keyframes bat-fly {
    0% { transform: translateX(-20px) translateY(0); opacity: 0; }
    10% { opacity: var(--particle-opacity, 0.3); }
    50% { transform: translateX(50vw) translateY(-15px); }
    90% { opacity: var(--particle-opacity, 0.3); }
    100% { transform: translateX(100vw) translateY(5px); opacity: 0; }
}

/* ── Sparkle ── */
.sparkle-particle {
    background: #FFD700;
    border-radius: 50%;
    animation: sparkle-blink ease-in-out infinite;
}
@keyframes sparkle-blink {
    0%, 100% { opacity: 0; transform: scale(0.5); }
    50% { opacity: 0.8; transform: scale(1.2); }
}

/* ── Fireworks ── */
.firework-particle {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--fw-color, #FFD700);
    animation: firework-burst ease-out infinite;
}
@keyframes firework-burst {
    0% { transform: scale(0); opacity: 0; box-shadow: none; }
    30% { transform: scale(1); opacity: 1; }
    50% {
        transform: scale(1.5);
        opacity: 0.8;
        box-shadow:
            8px -8px 0 var(--fw-color),
            -8px -8px 0 var(--fw-color),
            8px 8px 0 var(--fw-color),
            -8px 8px 0 var(--fw-color),
            0 -12px 0 var(--fw-color),
            0 12px 0 var(--fw-color);
    }
    100% {
        transform: scale(2);
        opacity: 0;
        box-shadow:
            16px -16px 0 transparent,
            -16px -16px 0 transparent,
            16px 16px 0 transparent,
            -16px 16px 0 transparent,
            0 -24px 0 transparent,
            0 24px 0 transparent;
    }
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
    .header-animation { display: none; }
}
</style>
